import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { AdminEquipmentService } from '@app/api/api';
import { AppState } from '@app/core/reducers';
import { currentUser } from '@app/core/auth';
import { User } from '@app/model/user';

declare var Chart: any;

@Component({
  selector: 'app-my-top-purchased-products',
  templateUrl: './my-top-purchased-products.component.html',
  styleUrls: ['./my-top-purchased-products.component.scss'],
})
export class MyTopPurchasedProductsComponent implements OnInit, AfterViewInit {
  @ViewChild('topProductsChart', { static: false }) topProductsChartRef!: ElementRef;

  loading = false;
  currentUserId: string = '';

  // Filters
  fromDate: string = '';
  toDate: string = '';
  topLimit: number = 10;
  topLimitOptions: number[] = [5, 10, 20, 50];

  // Data
  topProducts: any[] = [];

  private topProductsChart: any = null;

  private readonly palette: string[] = [
    '#36a3eb',
    '#ffb822',
    '#0abb87',
    '#f4516c',
    '#8950FC',
    '#1bc5bd',
    '#fd7e14',
    '#20c997',
    '#6f42c1',
    '#e83e8c',
    '#17a2b8',
    '#ffc107',
  ];

  constructor(
    private adminEquipmentService: AdminEquipmentService,
    private toastr: ToastrService,
    private ref: ChangeDetectorRef,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.store
      .pipe(select(currentUser), take(1))
      .subscribe((user: User) => {
        this.currentUserId = (user && user.id) || '';
        this.loadTopProducts();
      });
  }

  ngAfterViewInit() {}

  @HostListener('window:resize')
  onResize() {
    if (this.topProductsChart) {
      setTimeout(() => this.renderTopProductsChart(), 100);
    }
  }

  applyFilter() {
    this.loadTopProducts();
  }

  resetFilter() {
    this.fromDate = '';
    this.toDate = '';
    this.topLimit = 10;
    this.loadTopProducts();
  }

  loadTopProducts() {
    if (!this.currentUserId) {
      return;
    }
    this.loading = true;
    this.adminEquipmentService
      .apiAdminMyEquipmentTopPurchasedProductsGet(
        this.currentUserId,
        undefined,
        this.fromDate || undefined,
        this.toDate || undefined,
        this.topLimit
      )
      .subscribe(
        (res) => {
          this.loading = false;
          this.topProducts = (res && res.success && res.data) || [];
          this.ref.detectChanges();
          setTimeout(() => this.renderTopProductsChart(), 0);
        },
        () => {
          this.loading = false;
          this.topProducts = [];
          this.toastr.error('Lỗi khi tải sản phẩm mua nhiều nhất');
        }
      );
  }

  private truncateLabel(name: string, maxLength: number = 20): string {
    if (!name) return '';
    return name.length > maxLength ? name.slice(0, maxLength).trim() + '...' : name;
  }

  private renderTopProductsChart() {
    if (!this.topProductsChartRef) {
      return;
    }
    // Keep API's descending order so the highest revenue product renders at the top of the horizontal bar
    const data = (this.topProducts || []).slice(0, 5);
    const fullNames = data.map((p: any) => p.product_name);
    const labels = fullNames.map((name: string) => this.truncateLabel(name));
    const values = data.map((p: any) => p.total_amount);
    const colors = data.map((_: any, i: number) => this.palette[i % this.palette.length]);

    if (this.topProductsChart) {
      this.topProductsChart.destroy();
    }
    this.topProductsChart = new Chart(this.topProductsChartRef.nativeElement, {
      type: 'horizontalBar',
      data: {
        labels,
        datasets: [
          {
            label: 'Doanh thu',
            data: values,
            backgroundColor: colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{ ticks: { beginAtZero: true, callback: (v: number) => this.formatPrice(v) } }],
        },
        tooltips: {
          callbacks: {
            title: (tooltipItems: any) => fullNames[tooltipItems[0].index],
            label: (tooltipItem: any) => this.formatPrice(Number(tooltipItem.xLabel)) + ' đ',
          },
        },
      },
    });
  }

  formatPrice(price: number): string {
    return (price || 0).toLocaleString('vi-VN');
  }
}
